import { LocationGroupsSettingsQuery, useLocationGroupsSettingsQuery } from '@/buyers/_gen/gql'
import useGqlClient from '@/buyers/hooks/useGqlClient'
import useSession from '@/buyers/hooks/useSession'
import Action from '@/gf/components/Action'
import Card from '@/gf/components/Card'
import Tabs from '@/gf/components/next/Tabs'
import { useState } from 'react'
import CreateLocationGroupModal from './CreateLocationGroupModal'
import LocationGroupLocationsTable from './LocationGroupLocationsTable'
import LocationGroupsTable from './LocationGroupsTable'

export type LocationGroupUser = NonNullable<LocationGroupsSettingsQuery['org']>['users'][number]

export type LocationGroup = NonNullable<
  LocationGroupsSettingsQuery['org']
>['shippingLocationGroups'][number]

export type OrgLocation = NonNullable<LocationGroupsSettingsQuery['org']>['locations'][number]

const USERS_TAB = 'Users'
const LOCATIONS_TAB = 'Locations'

const LocationGroupsSettings = () => {
  const { organization } = useSession()
  const orgId = organization.id
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [selectedTab, setSelectedTab] = useState<typeof USERS_TAB | typeof LOCATIONS_TAB>(USERS_TAB)

  const queryResult = useLocationGroupsSettingsQuery({
    variables: { orgId },
    client: useGqlClient(),
  })

  const { loading, error, data } = queryResult
  const org = data?.org

  if (loading) {
    return (
      <Card title="Location Groups">
        <div className="p-4 text-gray-600">Loading...</div>
      </Card>
    )
  }

  if (error) {
    return (
      <Card title="Location Groups">
        <div className="p-4 text-red-600">Error loading location groups: {error.message}</div>
      </Card>
    )
  }

  if (!org) {
    return (
      <Card title="Location Groups">
        <div className="p-4 text-gray-600">Organization not found</div>
      </Card>
    )
  }

  const users = org.users || []
  const locationGroups = org.shippingLocationGroups || []
  const locations = org.locations || []

  // Show empty state if there are no location groups
  if (locationGroups.length === 0) {
    return (
      <>
        <Card title="Location Groups">
          <div className="pb-6 px-3 pt-3 flex grow justify-center items-center">
            <div className="max-w-md text-center flex flex-col gap-y-4 items-center">
              <h3 className="text-lg font-medium text-gray-900">No Location Groups</h3>
              <p className="text-gray-600 leading-normal">
                Assign Users to Location Groups to distribute their Requests across your team of
                Parts Coordinators.
              </p>
              <Action.P color="blue" onClick={() => setShowCreateModal(true)}>
                Create Location Group
              </Action.P>
            </div>
          </div>
        </Card>

        {showCreateModal && (
          <CreateLocationGroupModal
            orgId={orgId}
            onClose={() => setShowCreateModal(false)}
            onSuccess={() => {
              queryResult.refetch()
              setShowCreateModal(false)
            }}
          />
        )}
      </>
    )
  }

  return (
    <>
      <Card
        className="flex flex-col gap-y-6 overflow-hidden"
        title="Location Groups"
        subtitle={
          <span className="text-gray-500 text-sm">
            Assign Users to Location Groups to distribute their Requests across your team of Parts
            Coordinators.
          </span>
        }
        headerButton={
          <Action.S onClick={() => setShowCreateModal(true)}>New Location Group</Action.S>
        }
      >
        <div className="flex gap-x-6 border-b border-gray-200 px-3 -mt-2">
          <Tabs
            tabs={[{ name: USERS_TAB }, { name: LOCATIONS_TAB }]}
            selectedTabName={selectedTab}
            onTabSelect={(name) =>
              setSelectedTab(name === LOCATIONS_TAB ? LOCATIONS_TAB : USERS_TAB)
            }
          />
        </div>

        {selectedTab === USERS_TAB ? (
          <LocationGroupsTable
            users={users}
            locationGroups={locationGroups}
            refetch={queryResult.refetch}
          />
        ) : (
          <LocationGroupLocationsTable
            locationGroups={locationGroups}
            locations={locations}
            refetch={queryResult.refetch}
          />
        )}
      </Card>

      {showCreateModal && (
        <CreateLocationGroupModal
          orgId={orgId}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            queryResult.refetch()
            setShowCreateModal(false)
          }}
        />
      )}
    </>
  )
}

export default LocationGroupsSettings
